import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash, Ruler } from "@phosphor-icons/react";

const CATEGORIES = ["travel", "fuel", "food", "hotel", "lodging", "local_conveyance", "internet", "office_supplies", "training", "client_entertainment", "other"];
const TIERS = [
  { v: "*", l: "All cities (default)" },
  { v: "metro", l: "Metro" },
  { v: "tier1", l: "Tier 1" },
  { v: "tier2", l: "Tier 2" },
  { v: "other", l: "Other" },
];

export default function ReimbursementPolicies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", city_tier: "*", category: "food", per_day_cap: "", per_km_rate: "", monthly_km_cap: "", receipt_required: true, grade: "", active: true };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/reimbursement-policies").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form };
    ["per_day_cap", "per_km_rate", "monthly_km_cap"].forEach((k) => {
      payload[k] = payload[k] === "" ? null : parseFloat(payload[k]);
    });
    if (!payload.name || !payload.category) { toast.error("Name + category required"); return; }
    try {
      if (editing) { await api.patch(`/reimbursement-policies/${editing}`, payload); toast.success("Policy updated"); }
      else { await api.post("/reimbursement-policies", payload); toast.success("Policy created"); }
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || "", city_tier: r.city_tier || "*", category: r.category || "food",
      per_day_cap: r.per_day_cap ?? "", per_km_rate: r.per_km_rate ?? "",
      monthly_km_cap: r.monthly_km_cap ?? "", receipt_required: r.receipt_required !== false,
      grade: r.grade || "", active: r.active !== false,
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this policy row?")) return;
    try { await api.delete(`/reimbursement-policies/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal">Payroll · Admin</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy">Reimbursement policies</h1>
          <p className="text-sm text-zinc-500 mt-2">Define per-day TA/DA caps and per-km fuel rates. Rules can be scoped by city tier and pay-grade. Claims auto-validate against these caps.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm text-white" data-testid="policy-add">
              <Plus size={16} className="mr-1.5" /> Add policy
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit policy" : "Create policy"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Policy name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" placeholder="e.g. Metro food per-day cap" />
              </div>
              <div><Label className="text-xs">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">City tier</Label>
                <Select value={form.city_tier} onValueChange={(v) => setForm({ ...form, city_tier: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Per-day cap (₹)</Label>
                <Input type="number" value={form.per_day_cap} onChange={(e) => setForm({ ...form, per_day_cap: e.target.value })} className="rounded-sm" />
              </div>
              <div><Label className="text-xs">Per-km rate (₹/km, fuel)</Label>
                <Input type="number" step="0.1" value={form.per_km_rate} onChange={(e) => setForm({ ...form, per_km_rate: e.target.value })} className="rounded-sm" />
              </div>
              <div><Label className="text-xs">Monthly km cap</Label>
                <Input type="number" value={form.monthly_km_cap} onChange={(e) => setForm({ ...form, monthly_km_cap: e.target.value })} className="rounded-sm" />
              </div>
              <div><Label className="text-xs">Pay grade (optional)</Label>
                <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="rounded-sm" placeholder="M3, L4, etc." />
              </div>
              <div className="col-span-2 flex items-center gap-4 pt-2">
                <label className="text-xs inline-flex items-center gap-1.5"><input type="checkbox" checked={form.receipt_required} onChange={(e) => setForm({ ...form, receipt_required: e.target.checked })} /> Receipt required</label>
                <label className="text-xs inline-flex items-center gap-1.5"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-adb-navy text-white rounded-sm" data-testid="policy-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Name</th><th>Category</th><th>City tier</th><th>Grade</th><th>Per-day ₹</th><th>Per-km ₹</th><th>Monthly km cap</th><th>Receipt?</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-sm">No policies yet. Add one — TA/DA caps auto-validate on every claim.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="text-sm">{r.name}</td>
                <td className="text-xs uppercase">{r.category}</td>
                <td className="text-xs">{r.city_tier === "*" ? "all" : r.city_tier}</td>
                <td className="text-xs">{r.grade || "—"}</td>
                <td className="font-mono-plex text-xs">{r.per_day_cap ?? "—"}</td>
                <td className="font-mono-plex text-xs">{r.per_km_rate != null ? <span className="inline-flex items-center gap-1"><Ruler size={11} /> {r.per_km_rate}</span> : "—"}</td>
                <td className="font-mono-plex text-xs">{r.monthly_km_cap ?? "—"}</td>
                <td className="text-xs">{r.receipt_required ? "yes" : "no"}</td>
                <td><Badge variant="outline" className={`rounded-sm ${r.active !== false ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-50 border-zinc-200 text-zinc-500"}`}>{r.active !== false ? "active" : "off"}</Badge></td>
                <td className="text-xs whitespace-nowrap">
                  <button onClick={() => edit(r)} className="text-adb-navy hover:underline mr-2">Edit</button>
                  <button onClick={() => del(r.id)} className="text-red-600 hover:opacity-70"><Trash size={12} className="inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
